import { icon } from "./icons.js";
import { FacioFeedbackClient } from "./client.js";
import { startRuntimeMonitor } from "./runtime.js";
import { triggerPosition, widgetPlacement } from "./placement.js";
import type { FacioFeedbackEmbedSdk, HostConnection } from "./types.js";

interface EmbedHost extends Window {
  FacioFeedback?: FacioFeedbackClient;
  FacioFeedbackEmbed?: FacioFeedbackEmbedSdk;
}
const host: EmbedHost = window;
const source = new URL(import.meta.url);
const projectId = source.searchParams.get("project");
const configuredApiUrl = source.searchParams.get("api");
const apiUrl = (configuredApiUrl === null
  ? `${source.origin}/api`
  : new URL(configuredApiUrl, source.origin).href
).replace(/\/$/u, "");
function record(value: unknown): value is Record<string, unknown> { return typeof value === "object" && value !== null && !Array.isArray(value); }

// Loading the widget is inert until a host connects an authenticated identity.
if (host.FacioFeedbackEmbed === undefined && projectId !== null) {
  const embeddedProjectId = projectId;
  let connection: HostConnection | undefined;
  let stopRuntimeMonitor: (() => void) | undefined;
  let fallback: HTMLButtonElement | undefined;
  let status: HTMLSpanElement | undefined;
  let expiryTimer: number | undefined;
  let generation = 0;
  let appearance: Record<string, unknown> = {};
  function armExpiry(expiresAt: number | undefined): void {
    if (expiryTimer !== undefined) window.clearTimeout(expiryTimer);
    expiryTimer = expiresAt === undefined ? undefined : window.setTimeout(() => { void clear(); }, Math.min(2_147_483_647, expiresAt - Date.now()));
  }
  function removeFallback(): void { fallback?.remove(); status?.remove(); fallback = undefined; status = undefined; }
  async function clear(): Promise<void> {
    generation += 1;
    if (expiryTimer !== undefined) window.clearTimeout(expiryTimer);
    expiryTimer = undefined;
    stopRuntimeMonitor?.();
    stopRuntimeMonitor = undefined;
    connection = undefined;
    removeFallback();
    await host.FacioFeedback?.unload(); delete host.FacioFeedback;
  }
  function startMonitor(active: HostConnection): void {
    if (stopRuntimeMonitor !== undefined) return;
    const environment = typeof active.customData?.environment === "string"
      ? active.customData.environment
      : undefined;
    const release = typeof active.customData?.gitCommitSha === "string"
      ? active.customData.gitCommitSha
      : undefined;
    stopRuntimeMonitor = startRuntimeMonitor({
      apiUrl,
      projectId: embeddedProjectId,
      accessToken: () => connection?.accessToken,
      ...(environment ? { environment } : {}),
      ...(release ? { release } : {}),
    });
  }
  function reportError(error: unknown): void {
    if (connection === undefined) return;
    if (fallback === undefined) {
      fallback = document.createElement("button"); fallback.type = "button";
      fallback.dataset.facioFeedbackTrigger = "true"; document.body.append(fallback);
    }
    fallback.innerHTML = icon("pencil");
    const label = document.createElement("span"); label.textContent = typeof appearance.buttonText === "string" ? appearance.buttonText : "Feedback"; fallback.append(label);
    const color = typeof appearance.color === "string" && /^#[0-9a-f]{6}$/iu.test(appearance.color) ? appearance.color : "#fc3886";
    fallback.style.cssText = `position:fixed;${triggerPosition(appearance.placement)};z-index:2147483000;background:${color};color:white;border:0;padding:18px 11px;writing-mode:vertical-rl;display:flex;align-items:center;gap:10px;cursor:pointer`;
    const message = "Feedback is unavailable. Click to retry."; fallback.title = message;
    fallback.onclick = () => {
      status?.remove(); status = document.createElement("span"); status.setAttribute("role", "status"); status.textContent = message;
      status.style.cssText = "position:fixed;right:50px;top:50%;z-index:2147483000;background:white;color:#191a24;padding:12px;border:1px solid #ddd;border-radius:8px";
      document.body.append(status); void refresh();
    };
    window.dispatchEvent(new CustomEvent("facio-feedback:error", { detail: { message: error instanceof Error ? error.message : message } }));
  }
  async function refresh(): Promise<void> {
    const active = connection; const mine = ++generation;
    await host.FacioFeedback?.unload(); delete host.FacioFeedback; removeFallback();
    if (active === undefined || active !== connection) return;
    try {
      const headers = { authorization: `Bearer ${active.accessToken}` };
      const publicResponse = await fetch(`${apiUrl}/v1/projects/${encodeURIComponent(embeddedProjectId)}/widget-appearance`, { cache: "no-store" });
      if (!publicResponse.ok) throw new Error(`Feedback appearance unavailable (${publicResponse.status})`);
      const publicConfig: unknown = await publicResponse.json();
      if (!record(publicConfig)) throw new Error("Invalid Feedback appearance");
      if (mine !== generation || active !== connection) return;
      appearance = publicConfig; if (appearance.enabled === false) return;
      const response = await fetch(`${apiUrl}/v1/projects/${encodeURIComponent(embeddedProjectId)}/widget-config`, { cache: "no-store", headers });
      if (response.status === 401 || response.status === 403) { await clear(); return; }
      if (!response.ok) throw new Error(`Feedback configuration unavailable (${response.status})`);
      const config: unknown = await response.json();
      if (!record(config) || !record(config.widget) || !record(config.form) || typeof config.subject !== "string") throw new Error("Invalid Feedback configuration");
      if (mine !== generation || active !== connection || config.widget.enabled === false) return;
      const projectsResponse = await fetch(`${apiUrl}/v1/projects`, { cache: "no-store", headers });
      if (projectsResponse.status === 401 || projectsResponse.status === 403) { await clear(); return; }
      if (!projectsResponse.ok) throw new Error("Unable to load Feedback project name");
      const projects: unknown = await projectsResponse.json();
      const project = Array.isArray(projects) ? projects.find((item: unknown) => record(item) && item.id === embeddedProjectId) as unknown : undefined;
      if (!record(project) || typeof project.name !== "string") throw new Error("Feedback project is unavailable");
      if (mine !== generation || active !== connection) return;
      const issueTypes = config.form.issueTypes;
      host.FacioFeedback = new FacioFeedbackClient({ apiUrl, projectId: embeddedProjectId, token: "host-authenticated-session", subject: active.subject, projectName: project.name, consoleUrl: source.origin,
        ...(typeof project.workspaceSlug === "string" ? { workspaceSlug: project.workspaceSlug } : {}),
        ...(typeof project.slug === "string" ? { projectSlug: project.slug } : {}), accessToken: active.accessToken,
        ...(typeof active.workspaceLogoUrl === "string" ? { brandLogoUrl: active.workspaceLogoUrl } : {}),
        buttonText: typeof config.widget.buttonText === "string" ? config.widget.buttonText : "Feedback",
        ...(config.widget.maskScreenshot === true ? { maskScreenshot: true } : {}),
        ...(config.widget.maskReplay === true ? { maskReplay: true } : {}),
        ...(typeof config.widget.color === "string" && /^#[0-9a-f]{6}$/iu.test(config.widget.color) ? { buttonColor: config.widget.color } : {}),
        placement: widgetPlacement(config.widget.placement),
        ...(Array.isArray(issueTypes) && issueTypes.length > 0 && issueTypes.every((v): v is string => typeof v === "string") ? { issueTypes } : {}),
      });
      host.FacioFeedback.setCustomData({ source: location.origin, ...(active.customData ?? {}) });
      window.dispatchEvent(new Event("facio-feedback:ready"));
    } catch (error: unknown) { if (mine === generation && active === connection) reportError(error); }
  }
  async function connect(next: HostConnection): Promise<void> {
    if (!next.accessToken.trim() || !next.subject.trim()) throw new Error("A host authorization and subject are required");
    const expiresAt = next.expiresAt === undefined ? undefined : Date.parse(next.expiresAt);
    if (expiresAt !== undefined && (!Number.isFinite(expiresAt) || expiresAt <= Date.now())) { await clear(); return; }
    if (connection?.subject === next.subject && host.FacioFeedback !== undefined) {
      const mine = ++generation;
      const response = await fetch(`${apiUrl}/v1/projects/${encodeURIComponent(embeddedProjectId)}/widget-config`, {
        cache: "no-store", headers: { authorization: `Bearer ${next.accessToken}` },
      });
      if (mine !== generation) return;
      if (response.status === 401 || response.status === 403) throw new Error("Feedback authorization was rejected");
      if (!response.ok) throw new Error(`Feedback configuration unavailable (${response.status})`);
      host.FacioFeedback.updateAuthorization(next.accessToken);
      host.FacioFeedback.setCustomData({ source: location.origin, ...(next.customData ?? {}) });
      connection = next;
      startMonitor(next);
      armExpiry(expiresAt);
      window.dispatchEvent(new Event("facio-feedback:ready"));
      return;
    }
    await clear(); connection = next;
    startMonitor(next);
    armExpiry(expiresAt);
    await refresh();
  }
  async function connectPublic(): Promise<void> {
    // The installation snippet is useful on ordinary customer sites without a
    // server-side Facio integration.  The API only issues this credential for
    // the project's exact allowed Origin, and binds it to that same Origin.
    const started = generation;
    try {
      const response = await fetch(`${apiUrl}/v1/projects/${encodeURIComponent(embeddedProjectId)}/widget/public-session`, {
        method: "POST", credentials: "omit", cache: "no-store",
      });
      if (!response.ok) throw new Error(`Feedback public session unavailable (${response.status})`);
      const authorization: unknown = await response.json();
      if (!record(authorization) || typeof authorization.accessToken !== "string" || typeof authorization.subject !== "string" || typeof authorization.expiresAt !== "string") {
        throw new Error("Invalid Feedback public session");
      }
      // An explicit host connection always wins over a late anonymous bootstrap.
      if (started !== generation) return;
      await connect({ accessToken: authorization.accessToken, subject: authorization.subject, expiresAt: authorization.expiresAt });
    } catch (error: unknown) {
      if (started === generation) window.dispatchEvent(new CustomEvent("facio-feedback:error", { detail: { message: error instanceof Error ? error.message : "Feedback is unavailable" } }));
    }
  }
  host.FacioFeedbackEmbed = { connect, disconnect: clear, refresh };
  window.dispatchEvent(new Event("facio-feedback:embed-ready"));
  void connectPublic();
}
